import { Link } from 'react-router-dom';
import { usePortal } from '@/contexts/PortalContext';
import { useAuth } from '@/contexts/AuthContext';
import { 
  BookOpen, 
  GraduationCap, 
  Clock, 
  TrendingUp,
  ChevronRight,
  Play,
  CheckCircle,
  Calendar,
  Bot,
  Award,
  Timer,
  Bell,
  Megaphone,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export default function StudentDashboard() {
  const { user } = useAuth();
  const { enrollments, courses, resumeData, announcements, markAnnouncementRead, loading } = usePortal();

  // Calculate stats
  const totalCourses = courses.length;
  const completedLessons = courses.reduce((acc, c) => acc + (c.completed_lessons || 0), 0);
  const totalLessons = courses.reduce((acc, c) => acc + (c.total_lessons || 0), 0);
  const overallProgress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  // Format time display
  const formatTime = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] rounded-2xl p-6 lg:p-8 text-white">
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">
          Welcome back, {user?.name?.split(' ')[0] || 'Student'}!
        </h1>
        <p className="text-blue-100">
          Continue your learning journey in Workforce Intelligence
        </p>
      </div>

      {/* Quick Resume Card - Primary CTA */}
      {resumeData?.has_resume ? (
        <Card className="border-2 border-[#1E3A8A]/20 bg-gradient-to-r from-blue-50 to-white overflow-hidden" data-testid="quick-resume-card">
          <CardContent className="p-0">
            <div className="flex flex-col lg:flex-row">
              {/* Left: Course Info */}
              <div className="flex-1 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Play className="w-5 h-5 text-[#1E3A8A]" />
                  <span className="text-sm font-semibold text-[#1E3A8A] uppercase tracking-wide">
                    Quick Resume
                  </span>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-1">
                    {resumeData.course?.code} • {resumeData.module?.title}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {resumeData.lesson?.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {resumeData.course?.title}
                  </p>
                </div>

                {/* Progress & Time Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-500">Course Progress</p>
                      <p className="font-semibold text-gray-900">
                        {resumeData.course?.progress_percentage}%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Timer className="w-4 h-4 text-orange-500" />
                    <div>
                      <p className="text-xs text-gray-500">This Lesson</p>
                      <p className="font-semibold text-gray-900">
                        {formatTime(resumeData.estimated_completion_minutes) || '~25 min'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Course Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{resumeData.course?.completed_lessons} of {resumeData.course?.total_lessons} lessons</span>
                    <span>{resumeData.course?.remaining_lessons} remaining</span>
                  </div>
                  <Progress value={resumeData.course?.progress_percentage || 0} className="h-2" />
                </div>

                <Link to={`/portal/courses/${resumeData.course?.course_id}?lesson=${resumeData.lesson?.lesson_id}`}>
                  <Button className="w-full lg:w-auto bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" size="lg">
                    <Play className="w-4 h-4 mr-2" />
                    Resume Learning
                  </Button>
                </Link>
              </div>

              {/* Right: Visual Element */}
              <div className="hidden lg:flex lg:w-48 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] items-center justify-center">
                <div className="text-white text-center p-4">
                  <BookOpen className="w-16 h-16 mx-auto mb-2 opacity-80" />
                  <p className="text-sm font-medium opacity-90">
                    {formatTime(resumeData.estimated_remaining_minutes)}
                  </p>
                  <p className="text-xs opacity-70">to complete course</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : resumeData?.all_complete ? (
        <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-white" data-testid="all-complete-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Award className="w-8 h-8 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Congratulations!
                </h3>
                <p className="text-gray-600">
                  You've completed all available lessons. Check back soon for new content!
                </p>
              </div>
              <Link to="/portal/program">
                <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                  View Achievements
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : enrollments.length === 0 ? (
        <Card className="border-2 border-dashed border-gray-300 bg-gray-50" data-testid="no-enrollment-card">
          <CardContent className="p-6 text-center">
            <GraduationCap className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Start Your Learning Journey
            </h3>
            <p className="text-gray-500 mb-4">
              Enroll in a program to begin your workforce intelligence education
            </p>
            <Link to="/portal/program">
              <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90">
                Browse Programs
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : null}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-[#1E3A8A]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Programs</p>
                <p className="text-2xl font-bold text-gray-900">{enrollments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Courses</p>
                <p className="text-2xl font-bold text-gray-900">{totalCourses}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Lessons Done</p>
                <p className="text-2xl font-bold text-gray-900">{completedLessons}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Progress</p>
                <p className="text-2xl font-bold text-gray-900">{overallProgress}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Courses List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">My Courses</CardTitle>
              <Link to="/portal/courses" className="text-sm text-[#1E3A8A] hover:underline flex items-center">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardContent>
              {courses.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No courses yet</p>
                  <p className="text-sm text-gray-400">Enroll in a program to start learning</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {courses.slice(0, 4).map((course) => (
                    <Link 
                      key={course.course_id}
                      to={`/portal/courses/${course.course_id}`}
                      className="block"
                    >
                      <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-100 hover:border-[#1E3A8A]/30 hover:bg-gray-50 transition-colors">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-lg flex items-center justify-center text-white font-bold">
                          {course.code?.split('-')[0] || 'C'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{course.title}</h4>
                          <p className="text-sm text-gray-500">{course.code}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <Progress value={course.progress_percentage} className="flex-1 h-2" />
                            <span className="text-sm font-medium text-gray-600">
                              {Math.round(course.progress_percentage)}%
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Program Summary */}
          {enrollments.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold">My Program</CardTitle>
              </CardHeader>
              <CardContent>
                {enrollments.map((enrollment) => (
                  <div key={enrollment.enrollment_id} className="mb-4 last:mb-0">
                    <h4 className="font-medium text-gray-900">{enrollment.program_title}</h4>
                    <div className="mt-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Progress</span>
                        <span className="font-medium">{Math.round(enrollment.progress_percentage)}%</span>
                      </div>
                      <Progress value={enrollment.progress_percentage} className="h-2" />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {enrollment.completed_lessons} of {enrollment.total_lessons} lessons
                    </p>
                  </div>
                ))}
                <Link to="/portal/program">
                  <Button variant="outline" className="w-full mt-4">
                    View Program Details
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Quick Links */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link 
                to="/portal/schedule"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Calendar className="w-5 h-5 text-[#1E3A8A]" />
                <span className="text-sm font-medium">Schedule</span>
                <span className="ml-auto text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Soon</span>
              </Link>
              <Link 
                to="/portal/ai-tutor"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Bot className="w-5 h-5 text-[#1E3A8A]" />
                <span className="text-sm font-medium">AI Tutor</span>
                <span className="ml-auto text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Soon</span>
              </Link>
            </CardContent>
          </Card>

          {/* Announcements */}
          {announcements && announcements.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-[#1E3A8A]" />
                    Announcements
                  </CardTitle>
                  {announcements.filter(a => !a.is_read).length > 0 && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
                      {announcements.filter(a => !a.is_read).length} new
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {announcements.slice(0, 3).map((announcement) => (
                  <div 
                    key={announcement.announcement_id}
                    className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                      !announcement.is_read 
                        ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' 
                        : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      if (!announcement.is_read) {
                        markAnnouncementRead(announcement.announcement_id);
                      }
                    }}
                  >
                    <div className="flex items-start gap-2">
                      {announcement.priority === 'urgent' ? (
                        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      ) : announcement.priority === 'high' ? (
                        <Bell className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                      ) : (
                        <Bell className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h5 className={`text-sm font-medium truncate ${!announcement.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {announcement.title}
                        </h5>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                          {announcement.content.substring(0, 100)}...
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(announcement.publish_at).toLocaleDateString()}
                          {announcement.target_title && ` • ${announcement.target_title}`}
                        </p>
                      </div>
                      {!announcement.is_read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0"></span>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
